import { Box, Button, TextField, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

export const StyledPage = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8),
}))

export const StyledTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
}))

export const StyledSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(4),
}))

export const StyledToolsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
}))

export const StyledImportButton = styled(Button)(({ theme }) => ({
  borderRadius: 999,
  textTransform: 'none',
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}))

export const StyledSearchWrapper = styled(Box)({
  position: 'relative',
  minWidth: 240,
  flexGrow: 1,
  maxWidth: 400,
})

export const StyledSearchInput = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: 999,
  },
}))

export const StyledSearchDropdown = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '110%',
  left: 0,
  right: 0,
  zIndex: 20,
  borderRadius: Number(theme.shape.borderRadius) * 2,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
  overflow: 'hidden',
}))

export const StyledSearchRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 1.5),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

export const StyledSearchThumb = styled('img')(({ theme }) => ({
  width: 32,
  height: 48,
  objectFit: 'cover',
  borderRadius: Number(theme.shape.borderRadius),
  flexShrink: 0,
  backgroundColor: theme.palette.background.default,
}))

export const StyledMoodRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  overflowX: 'auto',
  paddingBottom: theme.spacing(1),
  marginBottom: theme.spacing(3),
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
}))

export const StyledMoodAddChip = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75, 1.75),
  borderRadius: 999,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontFamily: theme.typography.fontFamily,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  border: `1px dashed ${theme.palette.divider}`,
  flexShrink: 0,
  transition: 'all 0.2s ease',
  '&:disabled': {
    opacity: 0.5,
    cursor: 'default',
  },
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover:not(:disabled)': {
      borderColor: '#8B1E3F',
    },
  },
}))

export const StyledPoolGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(3),
  minHeight: 120,
}))

export const StyledPoolItem = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: 80,
}))

export const StyledPoolPoster = styled('img')(({ theme }) => ({
  width: 80,
  height: 120,
  objectFit: 'cover',
  borderRadius: Number(theme.shape.borderRadius) * 2,
  border: `1px solid ${theme.palette.divider}`,
  display: 'block',
  backgroundColor: theme.palette.background.paper,
}))

export const StyledPoolPosterFallback = styled(Box)(({ theme }) => ({
  width: 80,
  height: 120,
  borderRadius: Number(theme.shape.borderRadius) * 2,
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 24,
  backgroundColor: theme.palette.background.paper,
}))

export const StyledRemoveBadge = styled('button')(({ theme }) => ({
  position: 'absolute',
  top: -8,
  right: -8,
  width: 22,
  height: 22,
  borderRadius: '50%',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  lineHeight: 1,
  color: theme.palette.common.white,
  backgroundColor: '#8B1E3F',
}))

export const StyledFiltersRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
}))

export const StyledSpinArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(4),
  borderRadius: Number(theme.shape.borderRadius) * 3,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}))

export const StyledSpotlight = styled(Box)(({ theme }) => ({
  width: 220,
  height: 330,
  borderRadius: Number(theme.shape.borderRadius) * 2,
  overflow: 'hidden',
  border: `2px solid ${theme.palette.divider}`,
  boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

export const StyledSpotlightImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
})

export const StyledSpinButton = styled(Button)<{ component?: React.ElementType; to?: string }>(({ theme }) => ({
  borderRadius: 999,
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '1.05rem',
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(5),
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  color: theme.palette.common.white,
  background: 'linear-gradient(135deg, #8B1E3F 0%, #4A0E1F 100%)',
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
  },
}))

export const StyledWinnerMeta = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  textAlign: 'center',
}))
